// Smoke Drive file picker — insert into Messages, or pick a PDF for Labels

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileOutlined, FolderOutlined } from '@ant-design/icons';
import {
    DRIVE_ITEM_TYPE,
    driveItemPathLabel,
    driveItemsById,
    searchDriveLibrary,
    subscribeDriveLibrary,
} from '../../services/operatorDriveService';
import { formatOperatorDriveBytes } from '../../services/operatorDriveFileService';
import { isShippingLabelPdfDriveItem } from '../../utils/shippingLabelPdfHandoff';
import './OperatorDrive.css';

/**
 * @param {{
 *   uid: string,
 *   open: boolean,
 *   onClose: () => void,
 *   onPick: (items: object[]) => void,
 *   title?: string,
 *   hint?: string,
 *   confirmLabel?: string,
 *   pdfOnly?: boolean,
 *   multiple?: boolean,
 * }} props
 */
export default function OperatorDrivePicker({
    uid,
    open,
    onClose,
    onPick,
    title = 'Insert from Smoke Drive',
    hint = 'Sends a card that opens the library file. It does not upload a second copy.',
    confirmLabel = 'Insert',
    pdfOnly = false,
    multiple = true,
}) {
    const [library, setLibrary] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    useEffect(() => {
        if (!open || !uid) return undefined;
        return subscribeDriveLibrary(uid, setLibrary);
    }, [open, uid]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setSelectedIds(new Set());
        }
    }, [open]);

    const files = useMemo(() => {
        const byId = driveItemsById(library);
        const pool = search.trim()
            ? searchDriveLibrary(library, search)
            : library.filter((item) => !item.trashed && item.type === DRIVE_ITEM_TYPE.FILE);
        return pool
            .filter((item) => item.type === DRIVE_ITEM_TYPE.FILE && !item.trashed)
            .filter((item) => (pdfOnly ? isShippingLabelPdfDriveItem(item) : true))
            .map((item) => ({ ...item, pathLabel: driveItemPathLabel(item, byId) }))
            .slice(0, 120);
    }, [library, search, pdfOnly]);

    if (!open) return null;

    const toggle = (id) => {
        setSelectedIds((prev) => {
            if (!multiple) return new Set([id]);
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const chosen = files.filter((item) => selectedIds.has(item.id));

    const card = (
        <div className="op-drive-picker" role="dialog" aria-label={title}>
            <div className="op-drive-picker__card">
                <p className="op-drive-picker__title">{title}</p>
                <p className="op-drive-picker__hint">{hint}</p>
                <input
                    className="op-msg-search"
                    type="search"
                    placeholder="Search My files and Shared team"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search Smoke Drive"
                />
                <ul className="op-drive-picker__list">
                    {files.length === 0 ? (
                        <li className="op-drive-picker__empty">
                            {pdfOnly ? 'No PDF files match.' : 'No files match.'}
                        </li>
                    ) : (
                        files.map((item) => (
                            <li key={item.id}>
                                <label className="op-drive-picker__row">
                                    <input
                                        type={multiple ? 'checkbox' : 'radio'}
                                        name={multiple ? undefined : 'op-drive-picker-single'}
                                        checked={selectedIds.has(item.id)}
                                        onChange={() => toggle(item.id)}
                                    />
                                    <span className="op-drive-picker__icon" aria-hidden="true">
                                        {item.type === DRIVE_ITEM_TYPE.FOLDER ? (
                                            <FolderOutlined />
                                        ) : (
                                            <FileOutlined />
                                        )}
                                    </span>
                                    <span className="op-drive-picker__meta">
                                        <span className="op-drive-picker__name" title={item.name}>
                                            {item.name}
                                        </span>
                                        <span
                                            className="op-drive-picker__path"
                                            title={`${item.pathLabel} · ${formatOperatorDriveBytes(item.sizeBytes)}`}
                                        >
                                            {item.pathLabel} ·{' '}
                                            {formatOperatorDriveBytes(item.sizeBytes)}
                                        </span>
                                    </span>
                                </label>
                            </li>
                        ))
                    )}
                </ul>
                <div className="op-drive-picker__actions">
                    <button type="button" className="op-msg-btn op-msg-btn--ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="op-msg-btn op-msg-btn--primary"
                        disabled={chosen.length === 0}
                        onClick={() => {
                            onPick(chosen);
                            onClose();
                        }}
                    >
                        {confirmLabel}
                        {multiple && chosen.length ? ` (${chosen.length})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return card;
    return createPortal(card, document.body);
}
